import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";

import { WEB_URL } from "@/lib/web-url";

export interface ReferralProgramData {
  isActive: boolean;
  balance: number;
  currency: string;
  referralCode: string | null;
  /** Share link — `${WEB_URL}?r=${code}`, built by `buildReferralLink`. The
   *  SAME string the share sheet copies, QR-encodes and sends to every channel;
   *  it used to be built twice (here on the PORTAL origin, which nothing
   *  consumed, and again in the sheet on the marketing origin). One builder now.
   *  Audience-neutral by design — apps/web resolves locale + country itself and
   *  the `?r=` capture works on any landing path. */
  referralLink: string | null;
  totalReferrals: number;
  loading: boolean;
}

interface WalletOverviewResponse {
  balance?: {
    referralEnabled?: boolean;
    referralCode?: string | null;
    available?: number;
    currency?: string;
    totalReferrals?: number;
  };
}

const DEFAULT: ReferralProgramData = {
  isActive: false,
  balance: 0,
  currency: "EUR",
  referralCode: null,
  referralLink: null,
  totalReferrals: 0,
  loading: true,
};

/**
 * The one share link for a referral code: the `apps/web` marketing origin +
 * `?r=<code>`, never the portal origin (`portal.pontimed.app?r=…` renders the
 * SPA shell, has no `?r=` capture and cannot set the attribution cookie — a
 * link built that way silently loses the referral). Exported so the hook and
 * the share sheet cannot disagree about what the user is sharing.
 */
export function buildReferralLink(referralCode: string): string {
  return `${WEB_URL.replace(/\/+$/, "")}?r=${referralCode}`;
}

/** Dispatch this event after toggling referral in the wallet to update the header instantly. */
export function dispatchReferralToggled(): void {
  window.dispatchEvent(new Event("referral-toggled"));
}

/**
 * Returns current user's referral program status and earnings.
 * isActive = true when user.referralEnabled is true AND user has a referral code.
 * Refetches on: custom "referral-toggled" event, tab focus (visibilitychange).
 */
export function useReferralProgram(): ReferralProgramData {
  const { user } = useSession();
  const [data, setData] = useState<ReferralProgramData>({ ...DEFAULT, loading: true });
  const fetchedRef = useRef(false);

  const doFetch = useCallback(() => {
    apiFetch
      .get<WalletOverviewResponse>("/api/account/wallet?tab=overview")
      .then((json) => {
        if (!json?.balance) {
          setData({ ...DEFAULT, loading: false });
          return;
        }
        const b = json.balance;
        const referralCode: string | null = b.referralCode ?? null;
        setData({
          isActive: !!b.referralEnabled && referralCode !== null,
          balance: typeof b.available === "number" ? b.available : 0,
          currency: b.currency ?? "EUR",
          referralCode,
          referralLink: referralCode ? buildReferralLink(referralCode) : null,
          totalReferrals: b.totalReferrals ?? 0,
          loading: false,
        });
      })
      .catch(() => setData({ ...DEFAULT, loading: false }));
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!user) {
      // Anonymous users have no referral data, but we still need to flip
      // loading off so banners gated on `!loading` can render.
      setData({ ...DEFAULT, loading: false });
      fetchedRef.current = false;
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    doFetch();
  }, [user, doFetch]);

  // Refetch when referral is toggled or user returns to tab
  useEffect(() => {
    if (!user) return;

    const onToggled = () => doFetch();
    const onVisibility = () => {
      if (document.visibilityState === "visible" && fetchedRef.current) doFetch();
    };

    window.addEventListener("referral-toggled", onToggled);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("referral-toggled", onToggled);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, doFetch]);

  return data;
}
